import { z } from "zod";
import { type Block, parseMarkdown, plainText } from "./markdown-parse.ts";
import { headingKeys } from "./wiki-headings.ts";

// Passage anchors (wiki-collaboration-ux D2): a thread or an instruction points at a quoted
// passage of a page as it reads, with a little context on each side and its section. The quote
// is tenant content, like comment text: thread views only, never audit, notifications or lists.

/** The longest quote kept, in characters (the selection is trimmed to it). */
export const PASSAGE_QUOTE_MAX = 500;
/** The context kept on each side of a quote. */
export const PASSAGE_CONTEXT_MAX = 64;
/** The longest section key: a heading slug (`headingSlug` cuts at 120). */
export const PASSAGE_SECTION_MAX = 200;

// No control characters but line breaks and tabs (selections can span lines).
const CONTROL = /\p{Cc}/u;
const withoutBreaks = (text: string) => text.replace(/[\t\n\r]/g, "");
const passageText = (max: number, min: number) =>
  z
    .string()
    .max(max)
    .refine((s) => s.trim().length >= min, { message: "A quote cannot be empty" })
    .refine((s) => !CONTROL.test(withoutBreaks(s)), { message: "No control characters" });

export const wikiPassageAnchorSchema = z.object({
  /** The selected text as it reads, 1 to 500 characters. */
  quote: passageText(PASSAGE_QUOTE_MAX, 1),
  /** Up to 64 characters before and after it; empty at the page's edges. */
  prefix: passageText(PASSAGE_CONTEXT_MAX, 0),
  suffix: passageText(PASSAGE_CONTEXT_MAX, 0),
  /** The enclosing heading's slug; null before the first heading. */
  section: z
    .string()
    .min(1)
    .max(PASSAGE_SECTION_MAX)
    .regex(/^[\p{L}\p{N}-]+$/u)
    .nullable(),
});
export type WikiPassageAnchor = z.infer<typeof wikiPassageAnchorSchema>;

function blockTexts(blocks: readonly Block[], out: string[]) {
  for (const block of blocks) {
    switch (block.type) {
      case "heading":
      case "paragraph":
      case "quote":
        out.push(plainText(block.content));
        break;
      case "code":
        out.push(block.text);
        break;
      case "list":
        for (const item of block.items) {
          out.push(plainText(item.content));
          blockTexts(item.children, out);
        }
        break;
      case "table":
        for (const row of [block.header, ...block.rows]) {
          for (const cell of row) {
            out.push(plainText(cell));
          }
        }
        break;
      default:
        break;
    }
  }
}

/** A page's lines as it reads (`readableText`'s), each marked when it is a heading's. */
export function readableLines(markdown: string): { text: string; heading: boolean }[] {
  const lines: { text: string; heading: boolean }[] = [];
  for (const block of parseMarkdown(markdown)) {
    const out: string[] = [];
    blockTexts([block], out);
    for (const text of out) {
      lines.push({ text, heading: block.type === "heading" });
    }
  }
  return lines;
}

/** A page's text as it reads, markup dropped, blocks separated by a line break. */
export function readableText(markdown: string): string {
  const out: string[] = [];
  blockTexts(parseMarkdown(markdown), out);
  return out.join("\n");
}

/**
 * Text compared without whitespace: a selection spanning blocks reads them without the break
 * between them, and the browser and the parser disagree on spaces around markup.
 */
const squeeze = (text: string) => text.normalize("NFC").replace(/\s+/g, "");

/**
 * Where `anchor` reads in `markdown`: `found` when its quote occurs in the page as it reads, and
 * `context` when one occurrence also has its prefix before it and its suffix after it. Whitespace
 * never counts. Deterministic and linear in the page's size.
 */
export function findPassage(
  markdown: string,
  anchor: Pick<WikiPassageAnchor, "quote" | "prefix" | "suffix">,
): { found: boolean; context: boolean } {
  const text = squeeze(readableText(markdown));
  const quote = squeeze(anchor.quote);
  if (!quote) {
    return { found: false, context: false };
  }
  const prefix = squeeze(anchor.prefix);
  const suffix = squeeze(anchor.suffix);
  let found = false;
  for (let at = text.indexOf(quote); at >= 0; at = text.indexOf(quote, at + 1)) {
    found = true;
    if (text.endsWith(prefix, at) && text.startsWith(suffix, at + quote.length)) {
      return { found, context: true };
    }
  }
  return { found, context: false };
}

// ── Re-anchoring on publish (wiki-collaboration-ux D2) ─────────────────────────────────────

/** The similarity a reworded passage must reach to be re-found (Dice on character bigrams). */
export const PASSAGE_SIMILARITY_MIN = 0.8;

/** A page's text as it reads (NFC), with each top-level section's range in it. */
export interface SectionedPage {
  /** The text as it reads, NFC: what the matching compares. */
  text: string;
  sections: { slug: string | null; start: number; end: number }[];
  /** The text as the page renders it (its own encoding), and each `text` offset's place in it. */
  raw?: string;
  rawAt?: number[];
}

/**
 * `markdown` parsed once for the re-anchoring of all its threads (`relocatePassage`): its text
 * as it reads, normalised to NFC first, so offsets and quotes agree whatever the page's encoding.
 */
export function sectionedPage(markdown: string): SectionedPage {
  const page = sectionedText(markdown);
  // Matching runs on NFC; quotes and context are cut from the text as the page renders it, so
  // the reading view (which compares characters as they are) still finds them.
  let text = "";
  const rawAt: number[] = [];
  const toText: number[] = [];
  let r = 0;
  for (const group of page.text.match(/\P{M}\p{M}*|\p{M}+/gsu) ?? []) {
    const composed = group.normalize("NFC");
    for (let k = 0; k < group.length; k++) {
      toText.push(text.length);
    }
    for (let k = 0; k < composed.length; k++) {
      rawAt.push(r);
    }
    text += composed;
    r += group.length;
  }
  rawAt.push(r);
  toText.push(text.length);
  return {
    text,
    raw: page.text,
    rawAt,
    sections: page.sections.map((s) => ({
      slug: s.slug,
      start: toText[s.start] ?? text.length,
      end: toText[s.end] ?? text.length,
    })),
  };
}

function sectionedText(markdown: string): SectionedPage {
  const blocks = parseMarkdown(markdown);
  const slugs = new Map(headingKeys(blocks).map((h) => [h.block, h.slug] as const));
  const sections: { slug: string | null; start: number; end: number }[] = [
    { slug: null, start: 0, end: 0 },
  ];
  let text = "";
  blocks.forEach((block, index) => {
    const slug = slugs.get(index);
    if (slug !== undefined) {
      sections.push({ slug, start: text.length, end: text.length });
    }
    const out: string[] = [];
    blockTexts([block], out);
    for (const part of out) {
      text += `${text ? "\n" : ""}${part}`;
    }
    const last = sections[sections.length - 1];
    if (last) {
      last.end = text.length;
    }
  });
  return { text, sections };
}

/** `text` folded by `keep` per character, with each kept unit's index in `text`. */
function folded(text: string, keep: (ch: string) => string): { s: string; at: number[] } {
  let s = "";
  const at: number[] = [];
  let i = 0;
  for (const ch of text) {
    // One offset per UTF-16 unit kept, as `indexOf` counts them (an astral character is two).
    const kept = keep(ch);
    for (let k = 0; k < kept.length; k++) {
      s += kept[k];
      at.push(i);
    }
    i += ch.length;
  }
  return { s, at };
}
const noSpace = (ch: string) => (/\s/u.test(ch) ? "" : ch);
const lettersAndDigits = (ch: string) => (/[\p{L}\p{N}]/u.test(ch) ? ch.toLowerCase() : "");

/** Every start of `needle` in `hay`. */
function occurrences(hay: string, needle: string): number[] {
  const found: number[] = [];
  if (!needle) {
    return found;
  }
  for (let at = hay.indexOf(needle); at >= 0; at = hay.indexOf(needle, at + 1)) {
    found.push(at);
  }
  return found;
}

/** Lower-cased, runs of spaces as one: what the similarity compares. */
const simple = (text: string) => text.toLowerCase().replace(/\s+/gu, " ").trim();
/** Character bigrams of `text`, counted. */
function bigrams(text: string): Map<string, number> {
  const counts = new Map<string, number>();
  const chars = Array.from(text);
  for (let i = 1; i < chars.length; i++) {
    const gram = `${chars[i - 1]}${chars[i]}`;
    counts.set(gram, (counts.get(gram) ?? 0) + 1);
  }
  return counts;
}
/** Dice's coefficient on character bigrams, 0 to 1. */
function dice(a: Map<string, number>, b: Map<string, number>): number {
  let left = 0;
  let right = 0;
  let shared = 0;
  for (const n of a.values()) {
    left += n;
  }
  for (const [gram, n] of b) {
    right += n;
    shared += Math.min(n, a.get(gram) ?? 0);
  }
  return left + right ? (2 * shared) / (left + right) : 0;
}

/** The longest section the similarity step searches, in characters (bounds its cost). */
const SIMILARITY_SECTION_MAX = 20_000;

/** Up to `max` code points of `text`, from its start or its end. */
const cut = (text: string, max: number, fromEnd = false) => {
  const points = Array.from(text);
  return (fromEnd ? points.slice(-max) : points.slice(0, max)).join("");
};

/**
 * Re-finds `anchor`, quoted from an earlier version, in `markdown` (D2): the quote with its
 * context, then the quote alone (in its section first, else once on the page), then the quote
 * with case, punctuation and spaces ignored (likewise), then the run of words of its section most
 * like it by character bigrams (Dice), at `PASSAGE_SIMILARITY_MIN` or above. The new anchor quotes the new
 * text; null when nothing is found. Deterministic, linear in the page for each step but the last,
 * which is bounded by the section's size.
 */
export function relocatePassage(
  page: string | SectionedPage,
  anchor: WikiPassageAnchor,
): WikiPassageAnchor | null {
  const steps = relocateSteps(page, anchor);
  for (;;) {
    const step = steps.next();
    if (step.done) {
      return step.value;
    }
  }
}

/**
 * `relocatePassage` for the server: the same search, yielding to other requests between bounded
 * chunks of the similarity step (one event loop serves the API's concurrent requests).
 */
export async function relocatePassageAsync(
  page: string | SectionedPage,
  anchor: WikiPassageAnchor,
): Promise<WikiPassageAnchor | null> {
  const steps = relocateSteps(page, anchor);
  for (;;) {
    const step = steps.next();
    if (step.done) {
      return step.value;
    }
    // setImmediate where the runtime has it (Node): setTimeout(0) is clamped to a millisecond.
    await new Promise<void>((resolve) =>
      typeof setImmediate === "function" ? setImmediate(resolve) : setTimeout(resolve, 0),
    );
  }
}

/** Candidate windows compared between two yields of the similarity step. */
const WINDOWS_PER_STEP = 256;

function* relocateSteps(
  page: string | SectionedPage,
  anchor: WikiPassageAnchor,
): Generator<void, WikiPassageAnchor | null> {
  const sectioned = typeof page === "string" ? sectionedPage(page) : page;
  const { text, sections } = sectioned;
  const raw = sectioned.raw ?? text;
  const at = (index: number) => sectioned.rawAt?.[index] ?? index;
  // The last section starting at or before `index` (the preamble's starts at 0).
  const sectionAt = (index: number) =>
    sections.reduce<string | null>((found, s) => (s.start <= index ? s.slug : found), null);
  // A slug may name several sections (two "## Notes"): each is the passage's own.
  const owns = sections.filter((s) => s.slug === anchor.section);
  const result = (start: number, end: number): WikiPassageAnchor => ({
    quote: cut(raw.slice(at(start), at(end)).trim(), PASSAGE_QUOTE_MAX),
    prefix: cut(
      raw.slice(Math.max(0, at(start) - PASSAGE_CONTEXT_MAX * 2), at(start)),
      PASSAGE_CONTEXT_MAX,
      true,
    ),
    suffix: cut(raw.slice(at(end), at(end) + PASSAGE_CONTEXT_MAX * 2), PASSAGE_CONTEXT_MAX),
    section: sectionAt(start),
  });
  const inOwn = (index: number) => owns.some((own) => index >= own.start && index < own.end);

  for (const keep of [noSpace, lettersAndDigits]) {
    const hay = folded(text, keep);
    const quote = folded(anchor.quote.normalize("NFC"), keep).s;
    const starts = occurrences(hay.s, quote);
    if (!starts.length) {
      continue;
    }
    const span = (a: number) => {
      const start = hay.at[a] ?? 0;
      const last = hay.at[a + quote.length - 1] ?? start;
      // Past the last kept character, a surrogate pair included.
      return result(start, last + ((text.codePointAt(last) ?? 0) > 0xffff ? 2 : 1));
    };
    // Empty context matches anywhere: it identifies nothing.
    if (keep === noSpace && (anchor.prefix.trim() || anchor.suffix.trim())) {
      const prefix = folded(anchor.prefix.normalize("NFC"), keep).s;
      const suffix = folded(anchor.suffix.normalize("NFC"), keep).s;
      const withContext = starts.find(
        (a) => hay.s.endsWith(prefix, a) && hay.s.startsWith(suffix, a + quote.length),
      );
      if (withContext !== undefined) {
        return span(withContext);
      }
    }
    const local = starts.filter((a) => inOwn(hay.at[a] ?? -1));
    if (local.length) {
      return span(local[0] as number);
    }
    if (starts.length === 1) {
      return span(starts[0] as number);
    }
  }

  // A reworded passage: the run of words of its own section(s) most like it, a few words
  // shorter or longer than the quote.
  const target = bigrams(simple(anchor.quote));
  const quoteWords = simple(anchor.quote).split(" ").filter(Boolean).length;
  let best: { start: number; end: number; score: number } | null = null;
  let compared = 0;
  for (const own of owns) {
    if (own.end - own.start > SIMILARITY_SECTION_MAX) {
      continue;
    }
    const spans = [...text.slice(own.start, own.end).matchAll(/\S+/gu)].map((m) => ({
      start: own.start + m.index,
      end: own.start + m.index + m[0].length,
    }));
    for (let i = 0; i < spans.length; i++) {
      for (let n = Math.max(1, quoteWords - 2); n <= quoteWords + 3; n++) {
        const first = spans[i];
        const last = spans[i + n - 1];
        if (!first || !last) {
          break;
        }
        if (++compared % WINDOWS_PER_STEP === 0) {
          yield;
        }
        const score = dice(target, bigrams(simple(text.slice(first.start, last.end))));
        if (score >= PASSAGE_SIMILARITY_MIN && (!best || score > best.score)) {
          best = { start: first.start, end: last.end, score };
        }
      }
    }
  }
  return best ? result(best.start, best.end) : null;
}
